import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { authClient } from '@portal-clients/auth';
import { UserModel, UserPreferencesModel } from '@portal-clients/user';

import { useSubmit } from '@frontend/hooks';
import { useI18n } from '@frontend/locales';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';

import { useRoot } from './root-context';

export interface UserContextState {
  user: UserModel;
  logout(): void;
  isLoggingOut: boolean;
  registerLogoutCallback(callback: LogoutCallback): UnregisterCallback;
  refetch(completed?: () => void): void;
  updateCache(update: Partial<UserModel>): void;
  updateUser(user: Partial<UserModel>): void;
  updatePreferences(preferences: Partial<UserPreferencesModel>): void;
}

export type LogoutCallback = (user: UserModel) => Promise<void>;
export type UnregisterCallback = () => void;

export const UserContext = createContext<UserContextState>({
  logout: () => {},
  isLoggingOut: false,
  registerLogoutCallback: () => () => {},
  refetch: () => {},
  updateCache: () => {},
  updateUser: () => {},
  updatePreferences: () => {},
  user: undefined as unknown as UserModel,
});

export interface OptionalUserContextState {
  user?: UserModel;
  logout(): void;
  isLoggingOut: boolean;
  refetch(completed?: () => void): void;
  updateCache(update: Partial<UserModel>): void;
  updateUser(user: Partial<UserModel>): void;
  updatePreferences(preferences: Partial<UserPreferencesModel>): void;
}

export interface UserProviderProps {
  children: ReactNode;
  user?: UserModel;
  setUser: React.Dispatch<React.SetStateAction<UserModel | undefined>>;
}

export const UserProvider = ({ children, ...props }: UserProviderProps) => {
  const queryClient = useQueryClient();
  const { removeLocalSession } = useRoot();
  const [logoutCallbacks, setLogoutCallbacks] = useState<
    { id: string; cb: LogoutCallback }[]
  >([]);
  const { defaultLocale } = usePortalPublicConfiguration();
  const { setLanguage } = useI18n();

  const { submit: logout, submitting: isLoggingOut } = useSubmit<void, void>({
    async onSubmit() {
      const result = await Promise.allSettled(
        logoutCallbacks.map((item) => item.cb(props.user!)),
      );

      result.forEach((res) => {
        if (res.status === 'rejected') {
          console.error('A logout callback failed', res.reason);
          captureException(res.reason);
        }
      });

      setLanguage(defaultLocale ?? 'en-GB');

      await authClient.logout();
      removeLocalSession();
    },
    onError(e) {
      console.error(e);
      setLanguage(defaultLocale ?? 'en-GB');
      // If we fail for some reason to logout we delete the local session
      // token so the user does not end up in a weird state where they can't
      // log out. The token will later be invalidated when it expires instead.
      removeLocalSession();
    },
  });

  useEffect(() => {
    if (props.user?.locale) {
      document.documentElement.lang = props.user.locale;
    }
  }, [props.user?.locale]);

  const updateCache = (update: Partial<UserModel>) => {
    queryClient.setQueryData(['user'], (state: UserModel) => {
      if (!state || !update) {
        return state;
      }

      return { ...state, ...update };
    });
  };

  const registerLogoutCallback = useCallback(
    (callback: LogoutCallback): UnregisterCallback => {
      const id = crypto.randomUUID();
      setLogoutCallbacks((prev) => [...prev, { id, cb: callback }]);

      return () => {
        setLogoutCallbacks((prev) => prev.filter((item) => item.id !== id));
      };
    },
    [],
  );

  return (
    <UserContext.Provider
      value={{
        user: props.user!,
        updateUser: (user) => {
          props.setUser((prev) => ({ ...prev!, ...user }));
        },
        updatePreferences: (preferences) => {
          props.setUser((prev) => ({
            ...prev!,
            preferences: { ...prev!.preferences!, ...preferences },
          }));
        },
        logout,
        isLoggingOut,
        registerLogoutCallback,
        updateCache,
        refetch: (completed) => {
          queryClient.invalidateQueries({ queryKey: ['user'] }).finally(() => {
            completed?.();
          });
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextState => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  if (!context.user) {
    throw new Error('User is undefined. Did you mean to use useOptionalUser?');
  }

  return context;
};

export const useOptionalUser = (): OptionalUserContextState => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return useContext(UserContext) as OptionalUserContextState;
};
